/**
 * Droits d'accès du backoffice — source unique de vérité côté interface.
 *
 * Principe : **l'API est l'autorité, cette interface la reflète.** Masquer une
 * entrée de menu ne protège rien ; c'est le contrôle côté serveur qui protège.
 * Ce fichier existe pour ne pas proposer à l'utilisateur ce qui échouera de
 * toute façon, et chaque ligne cite l'annotation `@PreAuthorize` dont elle est
 * tirée. Si l'API change, c'est ici qu'il faut répercuter.
 *
 * Rôles présents dans le realm Keycloak `filmika` (relevé le 2026-09-26) :
 * ADMIN, CONTENT_MANAGER, EDITOR, AUTHOR, REVIEWER, VIEWER, API_CLIENT.
 * `SUPER_ADMIN` était autorisé par le code alors qu'il n'existe pas — la
 * mention a été retirée. `CONTENT_MANAGER` a été créé le 2026-09-26 : il était
 * exigé par 19 endpoints de casting sans exister, et ne donnait donc accès à
 * rien.
 */

/** Rôle réservé aux abonnés de l'application mobile. */
export const ROLE_APPLICATION = "VIEWER";

/** Rôles donnant accès au backoffice. VIEWER en est exclu. */
export const ROLES_BACKOFFICE = [
  "ADMIN",
  "CONTENT_MANAGER",
  "EDITOR",
  "AUTHOR",
  "REVIEWER",
] as const;

export type RoleBackoffice = (typeof ROLES_BACKOFFICE)[number];

function normaliser(roles: string[] | undefined): string[] {
  return (roles ?? []).map((r) => String(r).toUpperCase());
}

/** L'utilisateur possède-t-il au moins un rôle backoffice ? */
export function hasBackofficeAccess(roles: string[] | undefined): boolean {
  const possedes = normaliser(roles);
  return ROLES_BACKOFFICE.some((r) => possedes.includes(r));
}

/** Le compte est-il un abonné de l'application, et rien d'autre ? */
export function estCompteApplication(roles: string[] | undefined): boolean {
  const possedes = normaliser(roles);
  return possedes.includes(ROLE_APPLICATION) && !hasBackofficeAccess(roles);
}

/** L'utilisateur possède-t-il l'un des rôles demandés ? */
export function possedeUnRole(
  roles: string[] | undefined,
  requis: readonly string[]
): boolean {
  const possedes = normaliser(roles);
  return requis.some((r) => possedes.includes(r.toUpperCase()));
}

/**
 * Rôles pouvant **consulter** chaque section, par clé de menu.
 *
 * La visibilité suit l'accès en lecture : une section reste visible même si
 * l'utilisateur ne peut pas y écrire, les boutons d'action étant de toute façon
 * refusés par l'API.
 *
 * Les cinq sections sans API dédiée dans `backoffice-bff` (Statistiques,
 * Notifications, Modération des commentaires, Tests A/B, Paramètres techniques)
 * sont réservées à ADMIN : les pages ne sont pas encore fonctionnelles, et les
 * exposer à tous les profils ne fait qu'encombrer la navigation.
 */
export const ROLES_PAR_SECTION: Record<string, readonly string[]> = {
  // Vue d'ensemble : agrège contenus, utilisateurs et paiements. Les
  // indicateurs qu'un rôle ne peut pas lire restent simplement à zéro, chaque
  // appel de la page tolérant l'échec.
  dashboard: ROLES_BACKOFFICE,

  // ContentController : lecture ouverte à ADMIN, CONTENT_MANAGER, EDITOR,
  // AUTHOR et REVIEWER ; écriture réservée à ADMIN, CONTENT_MANAGER et EDITOR ;
  // suppression à ADMIN seul.
  content: ROLES_BACKOFFICE,
  categories: ROLES_BACKOFFICE,
  types: ROLES_BACKOFFICE,

  // BackofficeCastingController : lecture ADMIN, CONTENT_MANAGER, EDITOR,
  // AUTHOR, REVIEWER ; écriture ADMIN et CONTENT_MANAGER.
  artists: ROLES_BACKOFFICE,

  // UserController : hasRole('ADMIN') or hasRole('EDITOR') or hasRole('AUTHOR').
  users: ["ADMIN", "EDITOR", "AUTHOR"],

  // AdminPaymentTransactionController et PlanController : lecture REVIEWER,
  // AUTHOR, EDITOR, ADMIN ; opérations sensibles réservées à ADMIN.
  // CONTENT_MANAGER en est absent — les paiements ne relèvent pas de la
  // gestion des contenus, et l'API le refuserait.
  subscriptions: ["ADMIN", "EDITOR", "AUTHOR", "REVIEWER"],

  // Sections sans API dédiée à ce jour.
  statistics: ["ADMIN"],
  notifications: ["ADMIN"],
  "comment-moderation": ["ADMIN"],
  // « ab-tests » n'est plus dans le menu (BO-09, 2026-09-27) : la regle est
  // retiree avec lui plutot que laissee morte. A rétablir avec l'entree.
  "technical-settings": ["ADMIN"],
  // Limites d'appareils et de profils par compte (UX-01).
  "account-limits": ["ADMIN"],
};

/** La section est-elle visible pour ces rôles ? */
export function sectionVisible(
  cle: string,
  roles: string[] | undefined
): boolean {
  const requis = ROLES_PAR_SECTION[cle];
  // Une section non déclarée reste visible : mieux vaut un menu de trop qu'une
  // section rendue introuvable par oubli.
  if (!requis) return true;
  return possedeUnRole(roles, requis);
}
